import {useEffect, useState} from 'react';
import {Box, IconButton, Popover, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {alpha, useTheme} from '@mui/material/styles';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
import {
  dismissFrontDeskStartPageHint,
  isFrontDeskStartPageHintDismissed,
} from 'src/utils/contextPreferences';

interface StartPageHintPopoverProps {
  anchorEl: HTMLElement | null;
}

export default function StartPageHintPopover({anchorEl}: StartPageHintPopoverProps) {
  const theme = useTheme();
  const account = useAppSelector(selectAccount);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!account.userId || !anchorEl || isFrontDeskStartPageHintDismissed(account.userId)) {
      setOpen(false);
      return;
    }
    const timer = window.setTimeout(() => setOpen(true), 400);
    return () => window.clearTimeout(timer);
  }, [account.userId, anchorEl]);

  const handleDismiss = () => {
    if (account.userId) dismissFrontDeskStartPageHint(account.userId);
    setOpen(false);
  };

  return (
    <Popover
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleDismiss}
      anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
      transformOrigin={{vertical: 'top', horizontal: 'left'}}
      marginThreshold={12}
      slotProps={{
        paper: {
          sx: {
            mt: 0.75,
            width: 280,
            p: 2,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          },
        },
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1}}>
        <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.5, pr: 1}}>
          You can also set Front Desk as your start page so you land here after signing in.
        </Typography>
        <IconButton
          size="small"
          aria-label="Dismiss start page hint"
          onClick={handleDismiss}
          sx={{mt: -0.5, mr: -0.75, color: 'text.secondary'}}
        >
          <CloseIcon sx={{fontSize: 16}} />
        </IconButton>
      </Box>
    </Popover>
  );
}
